/**
 * CashFlowChart Component - Phase 5 Implementation
 * Area chart for cash flow inflow/outflow visualization
 * Shows cash movements with positive/negative flow indicators
 */

import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  ReferenceLine
} from 'recharts';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig
} from '../../ui/chart';
import { Skeleton } from '../../ui/skeleton';
import { ArrowUp, ArrowDown, DollarSign } from 'lucide-react';
import { cn } from '../../../lib/utils';

interface CashFlowChartProps {
  data: any[];
  isLoading?: boolean;
  error?: string;
  className?: string;
}

const chartConfig = {
  inflow: {
    label: "Cash Inflow",
    color: "hsl(var(--chart-3))",
  },
  outflow: {
    label: "Cash Outflow", 
    color: "hsl(var(--chart-4))",
  },
  netFlow: {
    label: "Net Flow",
    color: "hsl(var(--chart-1))",
  },
} satisfies ChartConfig;

// Mock data for demonstration
const generateMockData = () => [
  { date: '2024-01-01', inflow: 85000, outflow: -65000, netFlow: 20000, balance: 120000 },
  { date: '2024-01-02', inflow: 92000, outflow: -58000, netFlow: 34000, balance: 154000 },
  { date: '2024-01-03', inflow: 78000, outflow: -72000, netFlow: 6000, balance: 160000 },
  { date: '2024-01-04', inflow: 105000, outflow: -68000, netFlow: 37000, balance: 197000 },
  { date: '2024-01-05', inflow: 88000, outflow: -83000, netFlow: 5000, balance: 202000 },
  { date: '2024-01-06', inflow: 115000, outflow: -55000, netFlow: 60000, balance: 262000 },
  { date: '2024-01-07', inflow: 95000, outflow: -78000, netFlow: 17000, balance: 279000 },
  { date: '2024-01-08', inflow: 125000, outflow: -62000, netFlow: 63000, balance: 342000 },
  { date: '2024-01-09', inflow: 82000, outflow: -88000, netFlow: -6000, balance: 336000 },
  { date: '2024-01-10', inflow: 108000, outflow: -71000, netFlow: 37000, balance: 373000 },
  { date: '2024-01-11', inflow: 96000, outflow: -59000, netFlow: 37000, balance: 410000 },
  { date: '2024-01-12', inflow: 132000, outflow: -75000, netFlow: 57000, balance: 467000 },
  { date: '2024-01-13', inflow: 89000, outflow: -92000, netFlow: -3000, balance: 464000 },
  { date: '2024-01-14', inflow: 118000, outflow: -66000, netFlow: 52000, balance: 516000 },
];

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3 min-w-[220px]">
        <p className="font-medium mb-2">{formatDate(label)}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center justify-between text-sm mb-1">
            <div className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{entry.name}:</span>
            </div>
            <span className={cn(
              "font-medium",
              entry.dataKey === 'netFlow' && entry.value < 0 && "text-red-600",
              entry.dataKey === 'netFlow' && entry.value > 0 && "text-green-600"
            )}>
              {entry.dataKey === 'outflow' && entry.value < 0 
                ? `-${formatCurrency(entry.value)}`
                : formatCurrency(entry.value)
              }
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const CashFlowChart: React.FC<CashFlowChartProps> = ({
  data,
  isLoading = false,
  error,
  className
}) => {
  // Use mock data if no data provided or in loading state
  const chartData = data.length > 0 ? data : generateMockData();

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <p className="text-destructive font-medium">Failed to load chart</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-6 w-32" />
          <div className="flex space-x-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
        <Skeleton className="h-64 w-full" />
        <div className="flex space-x-6 justify-center">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    );
  }

  // Calculate summary metrics
  const totalInflow = chartData.reduce((sum, item) => sum + item.inflow, 0);
  const totalOutflow = chartData.reduce((sum, item) => sum + Math.abs(item.outflow), 0);
  const netTotal = totalInflow - totalOutflow;
  const currentBalance = chartData[chartData.length - 1]?.balance || 0;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 dark:text-green-300">Total Inflow</p>
              <p className="text-xl font-bold text-green-800 dark:text-green-200">
                {formatCurrency(totalInflow)}
              </p>
            </div>
            <div className="text-green-600">
              <ArrowUp className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700 dark:text-red-300">Total Outflow</p>
              <p className="text-xl font-bold text-red-800 dark:text-red-200">
                {formatCurrency(totalOutflow)}
              </p>
            </div>
            <div className="text-red-600">
              <ArrowDown className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className={cn(
          "rounded-lg p-4 border",
          netTotal >= 0 
            ? "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800" 
            : "bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800"
        )}>
          <div className="flex items-center justify-between">
            <div>
              <p className={cn(
                "text-sm",
                netTotal >= 0 
                  ? "text-blue-700 dark:text-blue-300" 
                  : "text-orange-700 dark:text-orange-300"
              )}>
                Net Flow
              </p>
              <p className={cn(
                "text-xl font-bold",
                netTotal >= 0 
                  ? "text-blue-800 dark:text-blue-200" 
                  : "text-orange-800 dark:text-orange-200"
              )}>
                {netTotal >= 0 ? '+' : ''}{formatCurrency(netTotal)}
              </p>
            </div>
            <div className={cn(
              netTotal >= 0 ? "text-blue-600" : "text-orange-600"
            )}>
              <DollarSign className="h-6 w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Current Balance</p>
              <p className="text-xl font-bold">
                {formatCurrency(currentBalance)}
              </p>
            </div>
            <div className="text-muted-foreground">
              <DollarSign className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Cash Flow Chart */}
      <ChartContainer config={chartConfig} className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <defs>
              <linearGradient id="inflowGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-inflow)" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="var(--color-inflow)" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="outflowGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-outflow)" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="var(--color-outflow)" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <YAxis 
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              stroke="hsl(var(--muted-foreground))"
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<ChartLegendContent />} />
            
            <ReferenceLine y={0} stroke="hsl(var(--border))" strokeDasharray="2 2" />
            
            <Area
              type="monotone"
              dataKey="inflow"
              stackId="1"
              stroke="var(--color-inflow)"
              fill="url(#inflowGradient)"
              strokeWidth={2}
              name="Cash Inflow"
            />
            <Area
              type="monotone"
              dataKey="outflow"
              stackId="2"
              stroke="var(--color-outflow)"
              fill="url(#outflowGradient)"
              strokeWidth={2}
              name="Cash Outflow"
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};